import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import AdapterDateFns from '@date-io/date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  Box,
  FormControlLabel,
  FormGroup,
  FormLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Popover from '@mui/material/Popover';
import React, { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import UserService from 'src/services/user/UserService';
import { AppState, store } from 'src/store';
import { setSnackbarMessage } from 'src/store/snackbarMessage';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import DocManagementService from '../documentUpload/service/DocManagementService';
import { AuditFilterProps } from './types';

export interface IActionElement {
  text: string;
  value: boolean;
}

export interface IActionType {
  actionTypeAll: IActionElement;
  actionTypeDataEntry: IActionElement;
  actionTypeDataChange: IActionElement;
  actionTypeDataAccess: IActionElement;
  actionTypeComments: IActionElement;
  actionTypeWorkflow: IActionElement;
}

export interface IFilterDate {
  valueFromDate: Date | string;
  valueTooDate: Date | string;
}

const initialState: IActionType = {
  actionTypeAll: { text: 'All', value: true },
  actionTypeDataEntry: { text: 'Data Entry', value: true },
  actionTypeDataChange: { text: 'Data Change', value: true },
  actionTypeDataAccess: { text: 'Document Access', value: true },
  actionTypeComments: { text: 'Comments', value: true },
  actionTypeWorkflow: { text: 'Workflow', value: true }
};

const falseState: IActionType = {
  actionTypeAll: { text: 'All', value: false },
  actionTypeDataEntry: { text: 'Data Entry', value: false },
  actionTypeDataChange: { text: 'Data Change', value: false },
  actionTypeDataAccess: { text: 'Document Access', value: false },
  actionTypeComments: { text: 'Comments', value: false },
  actionTypeWorkflow: { text: 'Workflow', value: false }
};

const FilterMenu: React.FC<AuditFilterProps> = ({
  handlefilter,
  openFilterSearch,
  setOpenFilterSearch,
  setpaginationGrid
}): any => {
  const docService = new DocManagementService();
  const [user, setUser] = useState<string>('');
  const dispatch = useDispatch();
  const [openFilter, setFilterOpenState] = React.useState(openFilterSearch);
  const [anchorElFS, setAnchorElFS] = React.useState<null | HTMLElement>(null);
  const [date, setDate] = useState<IFilterDate>({ valueFromDate: '', valueTooDate: '' });
  const [filterData, setFilterData] = useState<IActionType>(initialState);
  const [usersList, setUsersList] = React.useState([]);
  const [init, setInit] = React.useState(false);
  const [userError, setUsersError] = React.useState(null);
  const [isProcessing, setProcessing] = React.useState(false);
  const userService = UserService.getInstance();
  const { preferedFormatForDatePicker, userPreferedDateFormat } = usePreferedDateFormat();
  const [errorUploadedDate, setErrorUploadedDate] = useState<string>('');
  const documentDetail: any = useSelector((state: AppState) => state.documentDetail);
  const selectedStudy = useSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Study);
  const selectedCountry = useSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Country);
  const selectedSite = useSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Site);

  let TmfLevel =
    (selectedStudy === '--' || selectedStudy === '') &&
    (selectedCountry === '--' || selectedCountry == '') &&
    (selectedSite === '--' || selectedSite === '')
      ? '--'
      : (selectedCountry === '--' || selectedCountry == '') && (selectedSite === '--' || selectedSite === '')
      ? 'Study'
      : selectedSite === '--' && selectedCountry != '--'
      ? 'Country'
      : 'Site';
  if (TmfLevel === '--') {
    TmfLevel = 'Study';
  }

  useEffect(() => {
    setFilterOpenState(openFilterSearch);
    getAllUsers();
  }, [openFilterSearch]);

  const getAllUsers = async () => {
    setProcessing(true);
    const response: any = await docService.getAllUsers(
      documentDetail?.data[0]?.tmf_doc_study ? documentDetail?.data[0]?.tmf_doc_study : null,
      documentDetail?.data[0]?.tmf_doc_level,
      documentDetail.data[0].tmf_artifact_name,
      documentDetail?.data[0]?.tmf_doc_country ? documentDetail?.data[0]?.tmf_doc_country : null,
      documentDetail?.data[0]?.tmf_doc_site ? documentDetail?.data[0]?.tmf_doc_site : null
    );
    setInit(true);
    if (response?.data) {
      setUsersList(response?.data);
      setUsersError(null);
    } else {
      setUsersError(response?.error);
    }
    setProcessing(false);
  };

  const updateDate = (key: keyof IFilterDate, val: any) => {
    setDate(prev => ({ ...prev, [key]: val }));
  };

  const handleCloseFilterSearch = () => {
    setOpenFilterSearch(false);
  };

  const menuItemChange = (key: keyof IActionType, val: boolean) => {
    if (key === 'actionTypeAll' && val === true) {
      setFilterData(initialState);
    } else if (key === 'actionTypeAll' && val === false) {
      setFilterData(falseState);
      setpaginationGrid(false);
    } else if (val === false) {
      setFilterData(prev => ({
        ...prev,
        [key]: { text: prev[key].text, value: val },
        actionTypeAll: { text: prev.actionTypeAll.text, value: false }
      }));
      setpaginationGrid(false);
    } else {
      setFilterData(prev => ({ ...prev, [key]: { text: prev[key].text, value: val } }));
      setpaginationGrid(false);
    }
  };

  const handleChangeUsers = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUser(e.target.value);
  };

  const handleInvalidUploadedDate = (err: any, value: any) => {
    if (err === null) setErrorUploadedDate('');
    if (err === 'minDate') {
      setErrorUploadedDate('From date cannot be more then From To');
    } else if (err === 'disableFuture') {
      setErrorUploadedDate('Dates cannot be a future dates');
    } else if (err === 'invalidDate') {
      setErrorUploadedDate('Invalid date(s)');
    }
  };

  const handleInvalidDate = () => {
    store.dispatch(setSnackbarMessage(errorUploadedDate, 'error'));
    return true;
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;

  const handleClearFunctionality = () => {
    setUser('');
    setDate({ valueFromDate: '', valueTooDate: '' });
    setFilterData(initialState);
  };
  return (
    <Popover
      open={openFilterSearch}
      anchorEl={anchorElFS}
      onClose={handleCloseFilterSearch}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      className="auditTrailPopover"
    >
      {/* <Menu
      id="search-filter"
      anchorEl={anchorElFS}
      // getContentAnchorEl={null}
      // keepMounted={false}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      open={openFilterSearch}
      onClose={handleCloseFilterSearch}
      className="FilterSMenu"
    > */}
      <Paper elevation={4} sx={{ p: 1, pl: 3, pr: 3, width: 580, maxWidth: '100%' }} className="filterAH">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div" sx={{ mb: 1 }}>
            Filters
          </Typography>
          <Button onClick={handleClearFunctionality}> clear</Button>
        </Box>

        <Grid container direction="row" justifyContent="space-between">
          <Grid item md={5}>
            <FormControl component="fieldset" variant="standard" margin="normal">
              <FormLabel component="legend" sx={{ mb: 1 }}>
                Action Type
              </FormLabel>
              <FormGroup>
                <Stack direction="column" spacing={1}>
                  {Object.entries(filterData).map(data => (
                    <FormControlLabel
                      key={data[0]}
                      sx={{ ml: 0 }}
                      control={
                        <Switch
                          checked={data[1].value}
                          size="small"
                          onChange={(_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                            menuItemChange(data[0] as keyof IActionType, checked);
                          }}
                        />
                      }
                      label={data[1].text}
                    />
                  ))}
                </Stack>
              </FormGroup>
            </FormControl>
          </Grid>
          <Grid item md={7}>
            <FormControl fullWidth margin="normal">
              <FormLabel id="">Date</FormLabel>
            </FormControl>
            <Grid container spacing={3} sx={{ mb: 1, display: 'flex' }}>
              <Grid item md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} variant="standard" className="custom-datePicker">
                  <DesktopDatePicker
                    label="From"
                    inputFormat={userPreferedDateFormat}
                    value={preferedFormatForDatePicker(date.valueFromDate)}
                    onError={(err: any, value: any) => handleInvalidUploadedDate(err, value)}
                    onChange={(d: any) => {
                      updateDate('valueFromDate', d);
                    }}
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        InputLabelProps={{ shrink: true }}
                        className="smalllabel"
                        variant="standard"
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} variant="standard" className="custom-datePicker">
                  <DesktopDatePicker
                    label="To"
                    inputFormat={userPreferedDateFormat}
                    value={preferedFormatForDatePicker(date.valueTooDate)}
                    minDate={date.valueFromDate}
                    onError={(err: any, value: any) => handleInvalidUploadedDate(err, value)}
                    onChange={(d: any) => {
                      updateDate('valueTooDate', d);
                    }}
                    className="smalllabel"
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        // error={date.valueTooDate < date.valueFromDate}
                        // helperText={
                        //   date.valueTooDate
                        //     ? date.valueTooDate < date.valueFromDate
                        //       ? `Invalid Date Range`
                        //       : ' '
                        //     : ' '
                        // }
                        InputLabelProps={{ shrink: true }}
                        variant="standard"
                        className="smalllabel"
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>

            <FormControl fullWidth>
              <FormLabel id="" sx={{ mb: 1 }}>
                User
              </FormLabel>
            </FormControl>
            <FormControl fullWidth>
              <TextField
                id="standard-select-filtervalue"
                select
                onChange={handleChangeUsers}
                variant="standard"
                InputLabelProps={{ shrink: true }}
                defaultValue={user}
                value={user}
                SelectProps={{
                  MenuProps: {
                    disableScrollLock: true,
                    PaperProps: {
                      style: {
                        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                        width: 250
                      }
                    }
                  }
                }}
              >
                {usersList?.length > 0 &&
                  usersList.map((user: any, index: number) => (
                    <MenuItem key={index} value={user.firstName + ' ' + user.lastName}>
                      {`${user.firstName}  ${user.lastName} (${user.email})`}
                    </MenuItem>
                  ))}
              </TextField>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item md={12} display="flex" justifyContent="flex-end">
            <Stack direction="row" spacing={2}>
              <Button
                variant="text"
                href=""
                onClick={() => {
                  errorUploadedDate === '' ? handlefilter(filterData, date, user) : handleInvalidDate();
                }}
              >
                Apply
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
      {/* </Menu> */}
    </Popover>
  );
};

export default memo(FilterMenu);
